import {
  format,
  formatISO,
  isAfter,
  isWithinInterval,
  parse,
  parseISO,
  setHours,
  setMinutes,
  startOfDay
} from 'date-fns';

import { AM, PM, SERVER_DATE_FORMAT } from '@nw/shared/config';

export const getTimeInMinutesAndSecs = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds - minutes * 60;
  let minuteString = `${minutes}`;
  if (minutes < 10) {
    minuteString = `0${minutes}`;
  }
  let secondString = `${seconds}`;
  if (seconds < 10) {
    secondString = `0${seconds}`;
  }
  return `${minuteString}:${secondString}`;
};

export function getServerDateFormat(date: Date | number): string {
  return format(date, SERVER_DATE_FORMAT);
}

export const getTodayDate = (): Date => {
  return new Date();
};

export const getFormattedDate = (
  date: Date | string | null,
  formatToConvert: string
): string | null => {
  if (date && typeof date === 'string') {
    const updatedDateFormat = new Date(date);
    return format(updatedDateFormat, formatToConvert);
  } else if (date && date instanceof Date) {
    return format(date, formatToConvert);
  }
  return null;
};

export const getFormatTimeWithMinutes = (timeString: string): string => {
  const timeMatch = timeString.match(/(\d{1,2})\s*(AM|PM)/i);

  if (!timeMatch) {
    throw new Error('Invalid time format');
  }

  const hours = timeMatch[1];
  const amPm = timeMatch[2].toUpperCase();

  return `${hours}:00${amPm}`;
};

const parseTimeAndFormat = (
  webinarDateTime: string,
  timeString: string
): string => {
  const timeMatch = timeString.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i);

  if (!timeMatch) {
    throw new Error('Invalid time format');
  }

  let hours = parseInt(timeMatch[1], 10);
  const minutes = timeMatch[2] ? parseInt(timeMatch[2], 10) : 0;
  const amPm = timeMatch[3].toUpperCase();
  if (amPm === AM && hours === 12) {
    hours = 0;
  } else if (amPm === PM && hours !== 12) {
    hours += 12;
  }

  const parsedWebinarDateTime = parseISO(webinarDateTime);
  const webinarDateTimeWithHoursSet = setHours(parsedWebinarDateTime, hours);
  const webinarDateTimeWithMinutesSet = setMinutes(
    webinarDateTimeWithHoursSet,
    minutes
  );

  return formatISO(webinarDateTimeWithMinutesSet);
};

export const getWebinarDateTimeFromSelectedSlot = (
  webinarDateTime: string,
  preferredSlot: string
): string => {
  const cleanedSlot = preferredSlot.trim().replace(/\s+/g, ' ');

  // Check if this is a time range with AM/PM (e.g., "6:00PM - 9:00PM")
  if (cleanedSlot.match(/\d{1,2}:\d{2}(AM|PM)\s*-\s*\d{1,2}:\d{2}(AM|PM)/i)) {
    const startTime = cleanedSlot.split('-')[0].trim();
    return parseTimeAndFormat(webinarDateTime, startTime);
  } else if (cleanedSlot.match(/\d{1,2}(AM|PM)\s*-\s*\d{1,2}(AM|PM)/i)) {
    const [startTime, endTime] = cleanedSlot
      .split('-')
      .map((time) => time.trim());
    const formattedStartTime = getFormatTimeWithMinutes(startTime);
    return parseTimeAndFormat(webinarDateTime, formattedStartTime);
  }

  const parts = cleanedSlot.split('-');
  const timeString = parts[parts.length - 1].trim();
  return parseTimeAndFormat(webinarDateTime, timeString);
};

export const getWeekDay = (weekNumber: number) => {
  switch (weekNumber) {
    case 0:
      return 'Sunday';
    case 1:
      return 'Monday';
    case 2:
      return 'Tuesday';
    case 3:
      return 'Wednesday';
    case 4:
      return 'Thrusday';
    case 5:
      return 'Friday';
    case 6:
      return 'Saturday';
    default:
      return '';
  }
};

export const getTimeZone = () =>
  new window.Intl.DateTimeFormat().resolvedOptions().timeZone;

const setHoursToZero = (date: Date) => {
  return date.setHours(0, 0, 0, 0);
};

export const isDateWithInRange = (
  date: Date,
  startDate: Date,
  endDate: Date
) => {
  const minDate = setHoursToZero(startDate);
  const maxDate = setHoursToZero(endDate);
  const currentDate = setHoursToZero(date);
  return isWithinInterval(currentDate, { start: minDate, end: maxDate });
};

export const getParsedDateFormat = (date: string) => {
  const parsedDate = parse(date, 'dd/MM/yyyy', new Date());
  return parsedDate;
};

export interface DateOption {
  label: string;
  value: string;
}

interface DateSelectionOption extends DateOption {
  max_visibility_time?: string;
}

export const filterDateOptionsByVisibility = (
  options: Array<DateOption | DateSelectionOption>,
  defaultMaxVisibilityTime?: string
): DateOption[] => {
  const today = new Date();
  const todayStart = startOfDay(today);

  return options
    .filter((option) => {
      // Parse the option's date value
      const optionDate = parse(option.value, 'dd/MM/yyyy', new Date());
      const optionStart = startOfDay(optionDate);

      // If the date is in the future, include it
      if (isAfter(optionStart, todayStart)) {
        return true;
      }

      // If it's today, check max visibility time
      if (optionStart.getTime() === todayStart.getTime()) {
        // Use option's max_visibility_time if available, otherwise use default
        const maxVisibilityTime =
          (option as DateSelectionOption).max_visibility_time ||
          defaultMaxVisibilityTime;

        if (!maxVisibilityTime) return true;

        // Parse time in HH:mm format and set it to today's date
        const [hours, minutes] = maxVisibilityTime.split(':').map(Number);
        const visibilityDateTime = setMinutes(setHours(today, hours), minutes);

        // Include if current time is before or equal to max visibility time
        return !isAfter(today, visibilityDateTime);
      }

      // If the date is in the past, exclude it
      return false;
    })
    .map((option) => ({
      label: option.label,
      value: option.value
    }));
};

export const getFormattedDateWithOrdinalSuffix = (date: Date): string => {
  const day = format(date, 'd');
  const month = format(date, 'MMMM');
  const time = format(date, 'h:mm a');

  const getOrdinalSuffix = (day) => {
    if (day >= 11 && day <= 13) return 'th';
    const lastDigit = day % 10;
    switch (lastDigit) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  return `${day}${getOrdinalSuffix(Number(day))} ${month}, ${time}`;
};
